import {
  AlertCircle,
  CheckCircle,
  FileText,
  Globe,
  Plus,
  Sparkles,
  Upload,
} from "lucide-react";
import React, { useState } from "react";
import { useTranslation } from "../../contexts/LanguageContext";
import apiService from "../../shared/apiService";
import { API_ENDPOINTS } from "../../shared/constants";
import { cn } from "../../utils/cn";
import { getPlatformConfig, PLATFORMS_LIST } from "../../utils/platformIcons";
import Badge from "../ui/Badge";
import Button from "../ui/Button";
import CharCounter from "../ui/CharCounter";
import Input from "../ui/Input";
import Modal from "../ui/Modal";
import Select from "../ui/Select";
import Textarea from "../ui/Textarea";

interface CreateContentModalProps {
  open: boolean;
  onClose: () => void;
  onCreate: (data: {
    content_name: string;
    content_type: "url" | "text" | "prompt" | "file";
    content_data: string;
    platforms: string[];
    business_type: string;
  }) => Promise<void>;
  onSuccess?: () => void;
  loading?: boolean;
}

/* ── Plataformas: mesma fonte de ícones do CreatePostModal (evita ícones
   e comportamento de cor divergentes entre os dois modais) ── */
const CONTENT_PLATFORM_IDS = [
  "linkedin",
  "facebook",
  "instagram",
  "google_my_business",
];
const platformOptions = PLATFORMS_LIST.filter((p) =>
  CONTENT_PLATFORM_IDS.includes(p.id),
).map((p) => ({
  id: p.id,
  name: p.id === "google_my_business" ? "Google" : p.name,
  icon: <p.icon className="h-4 w-4" />,
}));

/* ── Tipos de conteúdo (formato de origem) ── */
const contentTypes = [
  {
    id: "url" as const,
    label: "URL / Site",
    icon: <Globe className="h-4 w-4" />,
    iconColor: "text-blue-600",
    iconBg: "bg-blue-50",
    iconBorder: "border-blue-200",
  },
  {
    id: "text" as const,
    label: "Texto",
    icon: <FileText className="h-4 w-4" />,
    iconColor: "text-emerald-600",
    iconBg: "bg-emerald-50",
    iconBorder: "border-emerald-200",
  },
  {
    id: "prompt" as const,
    label: "Prompt IA",
    icon: <Sparkles className="h-4 w-4" />,
    iconColor: "text-purple-600",
    iconBg: "bg-purple-50",
    iconBorder: "border-purple-200",
  },
  {
    id: "file" as const,
    label: "Arquivo",
    icon: <Upload className="h-4 w-4" />,
    iconColor: "text-orange-600",
    iconBg: "bg-orange-50",
    iconBorder: "border-orange-200",
  },
];

interface FormState {
  content_name: string;
  content_type: "url" | "text" | "prompt" | "file";
  content_data: string;
  platforms: string[];
  business_type: string;
}

const MAX_CHARS: Record<string, number> = {
  url: 500,
  text: 5000,
  prompt: 2000,
  file: 0,
};

const CreateContentModal: React.FC<CreateContentModalProps> = ({
  open,
  onClose,
  onCreate,
  onSuccess,
  loading,
}) => {
  const { t } = useTranslation();
  const [form, setForm] = useState<FormState>({
    content_name: "",
    content_type: "url",
    content_data: "",
    platforms: ["linkedin", "facebook"],
    business_type: "b2b",
  });
  const [error, setError] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  // Vira `true` na primeira tentativa de envio com campos obrigatórios
  // vazios — liga o destaque visual (borda vermelha) nesses campos.
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);

  React.useEffect(() => {
    if (open) {
      const viralContent = localStorage.getItem(
        "viral_research_content_library",
      );
      if (viralContent) {
        setForm({
          content_name: t("Conteúdo Gerado por IA"),
          content_type: "text",
          content_data: viralContent,
          platforms: ["linkedin", "facebook"],
          business_type: "b2b",
        });
        localStorage.removeItem("viral_research_content_library");
      } else {
        setForm({
          content_name: "",
          content_type: "url",
          content_data: "",
          platforms: ["linkedin", "facebook"],
          business_type: "b2b",
        });
      }
      setError(null);
      setSelectedFile(null);
      setAttemptedSubmit(false);
    }
  }, [open]);

  React.useEffect(() => {
    const handleViralContent = (e: any) => {
      if (e.detail) {
        setForm((prev) => ({
          ...prev,
          content_name: prev.content_name || t("Conteúdo Gerado por IA"),
          content_type: "text",
          content_data: e.detail,
        }));
      }
    };
    window.addEventListener(
      "viral_research_content_library_ready",
      handleViralContent,
    );
    return () => {
      window.removeEventListener(
        "viral_research_content_library_ready",
        handleViralContent,
      );
    };
  }, []);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const maxSize = 25 * 1024 * 1024;
    if (file.size > maxSize) {
      setError(
        t("Arquivo muito grande ({size}MB). Tamanho máximo: 25MB", {
          size: (file.size / 1024 / 1024).toFixed(2),
        }),
      );
      return;
    }
    const allowedExtensions = [
      ".pdf",
      ".doc",
      ".docx",
      ".xls",
      ".xlsx",
      ".txt",
    ];
    const ext = file.name.toLowerCase().substring(file.name.lastIndexOf("."));
    if (!allowedExtensions.includes(ext)) {
      setError(
        t("Tipo não suportado: {ext}. Use PDF, Word, Excel ou TXT.", { ext }),
      );
      return;
    }
    setSelectedFile(file);
    setError(null);
  };

  const handleChange = (field: keyof FormState, value: any) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handlePlatformToggle = (platformId: string) => {
    setForm((prev) => ({
      ...prev,
      platforms: prev.platforms.includes(platformId)
        ? prev.platforms.filter((p) => p !== platformId)
        : [...prev.platforms, platformId],
    }));
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    setError(null);
    setAttemptedSubmit(true);

    if (!form.content_name.trim()) {
      setError(t("Nome do conteúdo é obrigatório"));
      return;
    }
    if (form.content_type !== "file" && !form.content_data.trim()) {
      setError(t("Conteúdo é obrigatório"));
      return;
    }
    if (form.content_type === "file" && !selectedFile) {
      setError(t("Selecione um arquivo para upload"));
      return;
    }
    if (form.platforms.length === 0) {
      setError(t("Selecione pelo menos uma plataforma"));
      return;
    }

    try {
      if (form.content_type === "file" && selectedFile) {
        setUploadingFile(true);
        await apiService.uploadFile(
          API_ENDPOINTS.CONTENT.UPLOAD_FILE,
          selectedFile,
          {
            content_name: form.content_name.trim(),
            platforms: JSON.stringify(form.platforms),
            business_type: form.business_type || "b2b",
          },
        );
        setUploadingFile(false);
        if (onSuccess) await onSuccess();
        onClose();
        return;
      }
      await onCreate(form);
    } catch (err: any) {
      setError(err.message || t("Erro ao criar conteúdo"));
      setUploadingFile(false);
    }
  };

  const maxChars = MAX_CHARS[form.content_type] || 0;
  const charCount = form.content_data.length;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t("Criar Novo Conteúdo")}
      subtitle={t("Adicione conteúdo à sua biblioteca para gerar posts com IA")}
      icon={<Plus className="h-5 w-5" />}
      headerVariant="gradient"
      maxWidth="max-w-3xl"
      footer={
        <div className="flex justify-end gap-3 w-full">
          <Button
            type="button"
            onClick={onClose}
            size="sm"
            variant="secondary"
            disabled={loading || uploadingFile}
          >
            {t("Cancelar")}
          </Button>
          <Button
            type="submit"
            form="create-content-form"
            size="sm"
            variant="primary"
            disabled={
              loading ||
              uploadingFile ||
              !form.content_name.trim() ||
              form.platforms.length === 0 ||
              (form.content_type === "file"
                ? !selectedFile
                : !form.content_data.trim())
            }
            isLoading={loading || uploadingFile}
            className="shadow-lg shadow-purple-500/20"
          >
            {uploadingFile
              ? t("Fazendo Upload...")
              : loading
                ? t("Criando...")
                : form.content_type === "file"
                  ? t("Upload e Criar")
                  : t("Criar Conteúdo")}
          </Button>
        </div>
      }
    >
      <form
        id="create-content-form"
        onSubmit={handleSubmit}
        className="p-0.5 space-y-5"
      >
        {/* Erro */}
        {error && (
          <div className="flex items-start gap-2.5 p-3.5 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm animate-shake">
            <div className="shrink-0 mt-0.5">
              <svg
                className="h-4 w-4 text-red-500"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <span className="font-medium">{error}</span>
          </div>
        )}

        {/* Nome */}
        <div className="space-y-2">
          <label className="block text-[11px] font-bold uppercase tracking-wider text-gray-400 px-1">
            {t("Nome do Conteúdo")} <span className="text-red-400">*</span>
          </label>
          <Input
            type="text"
            value={form.content_name}
            onChange={(e) => handleChange("content_name", e.target.value)}
            placeholder={t("Ex: Landing Page, Post LinkedIn Semanal...")}
            required
            error={
              attemptedSubmit && !form.content_name.trim()
                ? t("O nome do conteúdo é obrigatório")
                : undefined
            }
            inputSize="sm"
            className="bg-gray-50! focus:bg-white! transition-colors"
          />
        </div>

        {/* Tipo */}
        <div className="space-y-3">
          <label className="block text-[11px] font-bold uppercase tracking-wider text-gray-400 px-1">
            {t("Formato de Origem")}
          </label>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {contentTypes.map((type) => {
              const isActive = form.content_type === type.id;
              return (
                <button
                  key={type.id}
                  type="button"
                  onClick={() => handleChange("content_type", type.id)}
                  className={cn(
                    "flex items-center gap-2.5 px-3 py-2 rounded-xl border-2 transition-all duration-300 text-left",
                    "focus:outline-hidden focus-visible:ring-2 focus-visible:ring-purple-400 focus-visible:ring-offset-1",
                    isActive
                      ? "bg-purple-600 border-transparent text-white shadow-lg scale-[1.02]"
                      : "bg-gray-50 border-gray-200 text-gray-700 hover:border-purple-300 hover:bg-purple-50/50",
                  )}
                >
                  <div
                    className={cn(
                      "p-1.5 rounded-lg border transition-colors shrink-0",
                      isActive
                        ? "bg-white/20 border-white/20"
                        : `${type.iconBg} ${type.iconBorder}`,
                    )}
                  >
                    {React.cloneElement(type.icon as React.ReactElement<any>, {
                      className: cn(
                        "h-4 w-4",
                        isActive ? "text-white" : type.iconColor,
                      ),
                    })}
                  </div>
                  <span className="text-xs font-bold uppercase tracking-tight truncate">
                    {t(type.label)}
                  </span>
                  {isActive && (
                    <CheckCircle className="h-3.5 w-3.5 text-white/80 shrink-0 ml-auto" />
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Dados / Arquivo */}
        <div className="space-y-2">
          <div className="flex items-center justify-between px-1">
            <label className="block text-[11px] font-bold uppercase tracking-wider text-gray-400">
              {form.content_type === "url"
                ? t("Link do Site")
                : form.content_type === "text"
                  ? t("Corpo do Texto")
                  : form.content_type === "prompt"
                    ? t("Comandos de IA")
                    : t("Documento (PDF, DOCX, TXT)")}
              <span className="text-red-400 ml-1">*</span>
            </label>
            {maxChars > 0 && (
              <CharCounter current={charCount} hard={maxChars} />
            )}
          </div>

          {form.content_type === "file" ? (
            <div
              className={cn(
                "border-2 border-dashed rounded-2xl p-6 transition-all duration-300 relative group",
                selectedFile
                  ? "border-emerald-200 bg-emerald-50/50"
                  : attemptedSubmit
                    ? "border-red-300 bg-red-50/30 hover:border-red-400"
                    : "border-purple-200 bg-gray-50/50 hover:border-purple-300 hover:bg-purple-50/30",
              )}
            >
              {selectedFile ? (
                <div className="flex items-center gap-4">
                  <div className="p-3.5 bg-emerald-500 rounded-2xl text-white shadow-lg shadow-emerald-500/20">
                    <FileText className="h-6 w-6" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-gray-900 truncate">
                      {selectedFile.name}
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge
                        variant="success"
                        size="sm"
                        className="font-bold uppercase text-[9px]"
                      >
                        {t("Pronto para upload")}
                      </Badge>
                      <span className="text-[10px] text-gray-400 font-bold">
                        {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                      </span>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setSelectedFile(null)}
                    className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all"
                    title={t("Remover arquivo")}
                  >
                    <Plus className="h-5 w-5 rotate-45" />
                  </button>
                </div>
              ) : (
                <label
                  htmlFor="file-upload-modal"
                  className="cursor-pointer block text-center space-y-3"
                >
                  <div className="w-12 h-12 bg-white rounded-2xl shadow-xs border border-purple-200 flex items-center justify-center mx-auto group-hover:scale-110 transition-transform duration-300">
                    <Upload className="h-6 w-6 text-purple-600" />
                  </div>
                  <div>
                    <span className="text-sm font-bold text-gray-700 block">
                      {t("Clique para selecionar")}
                    </span>
                    <span className="text-xs text-gray-400">
                      {t("ou arraste para esta área")}
                    </span>
                  </div>
                  <p className="text-[10px] text-purple-600 font-bold uppercase tracking-widest bg-purple-50 inline-block px-3 py-1 rounded-full border border-purple-200">
                    {t("PDF, Word, TXT — Máx. 25MB")}
                  </p>
                  <input
                    id="file-upload-modal"
                    name="file-upload-modal"
                    type="file"
                    className="hidden"
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.txt"
                    onChange={handleFileSelect}
                  />
                </label>
              )}
            </div>
          ) : null}
          {form.content_type === "file" && attemptedSubmit && !selectedFile && (
            <p className="flex items-center gap-1 text-xs text-red-600 font-medium px-1">
              <AlertCircle className="h-3.5 w-3.5 shrink-0" />
              {t("Selecione um arquivo para upload")}
            </p>
          )}
          {form.content_type !== "file" && (
            <Textarea
              value={form.content_data}
              onChange={(e) => handleChange("content_data", e.target.value)}
              maxLength={maxChars || undefined}
              error={
                attemptedSubmit && !form.content_data.trim()
                  ? t("O conteúdo é obrigatório")
                  : undefined
              }
              className={cn(
                "p-4 rounded-2xl! focus:border-purple-500! text-sm bg-gray-50! focus:bg-white! placeholder:text-gray-400! leading-relaxed shadow-inner",
                attemptedSubmit && !form.content_data.trim()
                  ? "border-red-300!"
                  : "border-purple-200!",
                form.content_type === "text" || form.content_type === "prompt"
                  ? "min-h-[140px]!"
                  : "min-h-[80px]!",
              )}
              placeholder={
                form.content_type === "url"
                  ? "https://suaempresa.com.br"
                  : form.content_type === "prompt"
                    ? t(
                        "Ex: Crie post sobre [tema] para o público [persona]...",
                      )
                    : t("Insira o texto base aqui...")
              }
              required
            />
          )}
        </div>

        {/* Plataformas */}
        <div className="space-y-3">
          <div className="flex items-center justify-between px-1">
            <label className="block text-[11px] font-bold uppercase tracking-wider text-gray-400">
              {t("Distribuição Sugerida")}{" "}
              <span className="text-red-400">*</span>
            </label>
            <span className="text-[10px] font-bold text-purple-600 bg-purple-50 px-2 py-0.5 rounded-full border border-purple-200">
              {t("{count} Canais", { count: form.platforms.length })}
            </span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {platformOptions.map((platform) => {
              const isActive = form.platforms.includes(platform.id);
              const platformConfig = getPlatformConfig(platform.id);
              return (
                <button
                  key={platform.id}
                  type="button"
                  onClick={() => handlePlatformToggle(platform.id)}
                  className={cn(
                    "flex items-center justify-center gap-2 px-3 py-2 rounded-xl border-2 transition-all duration-300 group",
                    "focus:outline-hidden focus-visible:ring-2 focus-visible:ring-purple-400 focus-visible:ring-offset-1",
                    isActive
                      ? "bg-purple-600 border-transparent text-white shadow-lg scale-[1.02]"
                      : attemptedSubmit && form.platforms.length === 0
                        ? "bg-gray-50 border-red-300 text-gray-700 hover:border-red-400 hover:bg-red-50/50"
                        : "bg-gray-50 border-gray-200 text-gray-700 hover:border-purple-300 hover:bg-purple-50/50",
                  )}
                >
                  <div
                    className={cn(
                      "p-1.5 rounded-lg border transition-colors",
                      isActive
                        ? "bg-white/20 border-white/20"
                        : `${platformConfig.bgColor} ${platformConfig.borderColor}`,
                    )}
                  >
                    {React.cloneElement(
                      platform.icon as React.ReactElement<any>,
                      {
                        className: cn(
                          "h-4 w-4",
                          isActive ? "text-white" : platformConfig.color,
                        ),
                      },
                    )}
                  </div>
                  <span className="text-xs font-bold uppercase tracking-tight whitespace-nowrap">
                    {platform.name}
                  </span>
                  {isActive && (
                    <CheckCircle className="h-3.5 w-3.5 text-white/80" />
                  )}
                </button>
              );
            })}
          </div>
          {attemptedSubmit && form.platforms.length === 0 && (
            <p className="flex items-center gap-1 text-xs text-red-600 font-medium px-1">
              <AlertCircle className="h-3.5 w-3.5 shrink-0" />
              {t("Selecione pelo menos uma plataforma")}
            </p>
          )}
        </div>

        {/* Tipo de Negócio e Sucesso */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2">
          <div className="space-y-2">
            <label className="block text-[11px] font-bold uppercase tracking-wider text-gray-400 px-1">
              {t("Enfoque do Conteúdo")}
            </label>
            <Select
              options={[
                { value: "b2b", label: t("B2B (Empresas)") },
                { value: "b2c", label: t("B2C (Consumidor Final)") },
              ]}
              value={form.business_type}
              onChange={(v) => handleChange("business_type", v)}
              size="sm"
              className="border-purple-200!"
            />
          </div>

          <div className="hidden md:flex items-start gap-2 px-3.5 py-2.5 rounded-xl bg-linear-to-br from-indigo-900 to-purple-900 text-white shadow-xs">
            <Sparkles className="h-3.5 w-3.5 text-purple-300 shrink-0 mt-0.5" />
            <p className="text-[11px] leading-snug text-purple-100 font-medium">
              <span className="font-bold uppercase tracking-wider text-purple-200 text-[10px]">
                {t("IA Otimizada:")}
              </span>{" "}
              {t(
                "O sistema analisará seu conteúdo para extrair tópicos relevantes para cada plataforma.",
              )}
            </p>
          </div>
        </div>
      </form>
    </Modal>
  );
};

export default CreateContentModal;
